import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react'
import ToastContainer from '@/components/common/ToastContainer/ToastContainer'
import { ToastMessage } from '@/components/common/ToastContainer/ToastContainer'

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export function useToastContext() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToastContext must be used within ToastProvider')
  }
  return context
}

interface ToastProviderProps {
  children: ReactNode
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  const showToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' = 'info', duration = 3000) => {
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 9)
      setToasts((prev) => [{ id, message, type, duration }, ...prev])
    },
    []
  )

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])
 
  // Global unhandled rejection handler: show toast instead of letting app-level navigation happen
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      try {
        const reason: any = event.reason
        const message =
          reason?.response?.data?.message ||
          reason?.message ||
          (typeof reason === 'string' ? reason : '알 수 없는 오류가 발생했습니다.')
        showToast(message, 'error')
        // preventDefault to mark as handled
        event.preventDefault()
      } catch (e) {
        // ignore
      }
    }
    window.addEventListener('unhandledrejection', handler)
    return () => window.removeEventListener('unhandledrejection', handler)
  }, [showToast])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  )
}
